import L from "leaflet";
import { ILayer } from "../models";

/**
 * WFS Helper - Shared utilities for WFS feature handling
 */
export class WFSHelper {
    /**
     * Get bounding box from map bounds in EPSG:25832 format
     */
    static getBBoxFromMapBounds(mapRef: any): string | null {
        if (!mapRef || !mapRef.leafletElement) return null;

        const bounds = mapRef.leafletElement.getBounds();
        const sw = bounds.getSouthWest();
        const ne = bounds.getNorthEast();

        const crs = mapRef.leafletElement.options.crs;
        const swPoint = crs.project(L.latLng(sw.lat, sw.lng));
        const nePoint = crs.project(L.latLng(ne.lat, ne.lng));

        return `${swPoint.x},${swPoint.y},${nePoint.x},${nePoint.y},EPSG:25832`;
    }

    /**
     * Parse GML XML response to GeoJSON format
     */
    static parseGMLToGeoJSON(gmlText: string, mapRef: any): any {
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(gmlText, "text/xml");

        // Check for parsing errors
        const parseError = xmlDoc.querySelector('parsererror');
        if (parseError) {
            console.error('GML parsing error:', parseError.textContent);
            return null;
        }

        const features: any[] = [];
        const featureMembers = xmlDoc.querySelectorAll('gml\\:featureMember, featureMember');

        featureMembers.forEach(featureMember => {
            try {
                // Get polygon coordinates from gml:posList
                const posList = featureMember.querySelector('gml\\:posList, posList');
                if (!posList || !posList.textContent) return;

                // Parse coordinates - they come as "x1 y1 x2 y2 x3 y3 ..."
                const coords = posList.textContent.trim().split(/\s+/).map(Number);
                const coordinates: number[][] = [];

                for (let i = 0; i < coords.length; i += 2) {
                    const x = coords[i];     // Easting (EPSG:25832)
                    const y = coords[i + 1]; // Northing (EPSG:25832)

                    // Convert EPSG:25832 (UTM) to lat/lng using CRS
                    if (mapRef && mapRef.leafletElement) {
                        const crs = mapRef.leafletElement.options.crs;
                        const point = L.point(x, y);
                        const latLng = crs.unproject(point);
                        coordinates.push([latLng.lng, latLng.lat]);
                    }
                }

                if (coordinates.length > 0) {
                    // Extract properties from the feature
                    const properties: any = {};
                    const children = featureMember.children[0]?.children || [];

                    for (let i = 0; i < children.length; i++) {
                        const child = children[i];
                        const tagName = child.localName || child.tagName.split(':').pop() || '';
                        const value = child.textContent ? child.textContent.trim() : '';

                        if (tagName !== 'SHAPE' && value) {
                            properties[tagName] = value;

                            // Keep a stable key for popup rendering regardless of source casing.
                            if (tagName.toLowerCase() === 'wasserrahmenrichtlinie_') {
                                properties.Wasserrahmenrichtlinie_ = value;
                            }
                        }
                    }

                    // Look for URLs in various possible properties
                    const urlValue = String(
                        properties.Wasserrahmenrichtlinie_ || 
                        properties.wasserrahmenrichtlinie_ ||
                        properties.URL ||
                        properties.url ||
                        properties.Link ||
                        properties.link ||
                        properties.Verweis ||
                        properties.verweis ||
                        ''
                    );
                    const urls = WFSHelper.extractUrls(urlValue);
                    
                    // If no URLs found yet, search all property values for URLs
                    if (urls.length === 0) {
                        for (const key in properties) {
                            const value = String(properties[key] || '');
                            if (value.includes('http')) {
                                const foundUrls = WFSHelper.extractUrls(value);
                                if (foundUrls.length > 0) {
                                    urls.push(...foundUrls);
                                }
                            }
                        }
                    }
                    
                    const firstCoordinate = coordinates[0] || [];
                    const featureSeed = `${urls.join('|')}|${coordinates.length}|${firstCoordinate[0] || 0}|${firstCoordinate[1] || 0}`;

                    properties._wfsFeatureColor = WFSHelper.getDeterministicFeatureColor(featureSeed);
                    properties._wfsUrls = urls;

                    features.push({
                        type: 'Feature',
                        properties,
                        geometry: {
                            type: 'Polygon',
                            coordinates: [coordinates]
                        }
                    });
                }
            } catch (error) {
                console.warn('Error parsing feature:', error);
            }
        });

        // A valid response with zero features (e.g. nothing in the current viewport) is not a parse failure.
        return {
            type: 'FeatureCollection',
            features
        };
    }

    /**
     * Extract URLs from a text value
     */
    static extractUrls(value: string): string[] {
        if (!value) return [];

        const urlMatches = value.match(/https?:\/\/[^\s,;|]+/gi) || [];
        const normalizedUrls = urlMatches.map(url => url.replace(/[),.;]+$/, '').trim());

        return normalizedUrls.filter((url, index, self) => !!url && self.indexOf(url) === index);
    }

    /**
     * Generate a deterministic color from a seed string
     */
    static getDeterministicFeatureColor(seed: string): string {
        const palette = [
            '#D81B60', '#1E88E5', '#43A047', '#FB8C00', '#6D4C41',
            '#00ACC1', '#C0CA33', '#8E24AA', '#3949AB', '#F4511E'
        ];

        let hash = 0;
        for (let i = 0; i < seed.length; i++) {
            hash = ((hash << 5) - hash) + seed.charCodeAt(i);
            hash |= 0;
        }

        return palette[Math.abs(hash) % palette.length];
    }

    /**
     * Fetch WFS features for enabled layers
     */
    static fetchWFSFeatures(
        mapRef: any,
        layers: ILayer[],
        onLayerFetched: (layerName: string, geoJSON: any) => void
    ): void {
        if (!mapRef || !mapRef.leafletElement) {
            console.warn('Map ref not available for WFS fetch');
            return;
        }

        const bbox = WFSHelper.getBBoxFromMapBounds(mapRef);
        const wfsLayers = layers.filter(l => l.type === "wfs" && l.enabled);

        wfsLayers.forEach(layer => {
            let url = `${layer.url}?service=WFS&version=1.1.0&request=GetFeature&typeName=${layer.layers}&outputFormat=GML3&srsName=EPSG:25832`;

            // Add BBOX filter if available
            if (bbox) {
                url += `&BBOX=${bbox}`;
            }

            fetch(url)
                .then(res => res.text())
                .then(text => {
                    const geoJSON = WFSHelper.parseGMLToGeoJSON(text, mapRef);

                    if (geoJSON) {
                        onLayerFetched(layer.name, geoJSON);
                    } else {
                        console.warn('Failed to parse WFS response for layer:', layer.name);
                        onLayerFetched(layer.name, null);
                    }
                })
                .catch((error) => {
                    console.error(`WFS fetch error for layer "${layer.name}":`, error);
                    onLayerFetched(layer.name, null);
                });
        });
    }

    /**
     * Create popup content for WFS feature with URLs
     */
    static createWFSPopupContent(feature: any): string {
        let urls = feature?.properties?._wfsUrls;
        
        if (!urls || !Array.isArray(urls) || urls.length === 0) {
            const rawUrls = 
                feature?.properties?.Wasserrahmenrichtlinie_ ||
                feature?.properties?.wasserrahmenrichtlinie_ ||
                feature?.properties?.URL ||
                feature?.properties?.url ||
                feature?.properties?.Link ||
                feature?.properties?.link ||
                feature?.properties?.Verweis ||
                feature?.properties?.verweis ||
                '';
            urls = WFSHelper.extractUrls(String(rawUrls));
        }
        
        if (!urls || urls.length === 0) {
            console.warn('No URLs found in feature properties:', feature?.properties);
            return '';
        }

        const color = feature?.properties?._wfsFeatureColor || '#d32f2f';
        const links = urls
            .map((url: string) => `<li><a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a></li>`)
            .join('');

        return `
            <div>
                <div style="display:flex;align-items:center;margin-bottom:8px;">
                    <span style="display:inline-block;width:12px;height:12px;background:${color};border:1px solid #333;margin-right:6px;"></span>
                    <span>${feature?.properties?.Gebietsname || feature?.properties?.NAME || ''}</span>
                </div>
                <ul style="margin:0;padding-left:16px;">${links}</ul>
            </div>
        `;
    }

    /**
     * Bind popup to WFS layer
     */
    static bindWFSPopup(feature: any, layer: any): void {
        const popupContent = WFSHelper.createWFSPopupContent(feature);
        if (popupContent) {
            layer.bindPopup(popupContent);
        } else {
            console.warn('No popup content generated for feature:', feature);
        }
    }

    /**
     * Get the distinct color/name/url combinations for features of a layer that are currently within the map bounds,
     * so the legend can reflect the actual areas shown (each area can have its own color) instead of a single swatch.
     */
    static getVisibleFeatureLegendEntries(
        mapRef: any,
        layerName: string,
        wfsFeatures: { [layerName: string]: any }
    ): Array<{ color: string; name: string; urls: string[] }> {
        const geoJson = wfsFeatures[layerName];
        if (!geoJson || !Array.isArray(geoJson.features)) return [];

        const mapBounds = mapRef && mapRef.leafletElement ? mapRef.leafletElement.getBounds() : null;

        const isFeatureVisible = (coordinates: any): boolean => {
            if (!mapBounds) return true;

            let visible = false;
            const inspect = (coords: any): void => {
                if (visible || !Array.isArray(coords)) return;

                if (coords.length >= 2 && typeof coords[0] === 'number' && typeof coords[1] === 'number') {
                    visible = mapBounds.contains(L.latLng(coords[1], coords[0]));
                    return;
                }

                coords.forEach((child: any) => inspect(child));
            };

            inspect(coordinates);
            return visible;
        };

        const entries = geoJson.features
            .filter((feature: any) => isFeatureVisible(feature?.geometry?.coordinates))
            .map((feature: any) => ({
                color: feature?.properties?._wfsFeatureColor || '#d32f2f',
                name: feature?.properties?.Gebietsname || feature?.properties?.NAME || '',
                urls: Array.isArray(feature?.properties?._wfsUrls) ? feature.properties._wfsUrls : []
            }));

        return entries.filter((entry, index, self) =>
            index === self.findIndex(e => e.color === entry.color && e.name === entry.name && e.urls.join('|') === entry.urls.join('|'))
        );
    }
}
